import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CreditCard, CheckCircle, Clock, XCircle, AlertCircle, Copy } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusIcons = {
  completed: <CheckCircle className="w-4 h-4 text-green-600" />,
  paid: <CheckCircle className="w-4 h-4 text-green-600" />,
  pending: <Clock className="w-4 h-4 text-yellow-600" />,
  initiated: <Clock className="w-4 h-4 text-blue-600" />,
  failed: <XCircle className="w-4 h-4 text-red-600" />,
  expired: <XCircle className="w-4 h-4 text-stone-500" />,
};

const statusColors = {
  completed: 'bg-green-100 text-green-800',
  paid: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  initiated: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800',
  expired: 'bg-stone-100 text-stone-600',
};

const AdminPayments = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/payments`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const totalRevenue = transactions
    .filter(t => t.payment_status === 'paid')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const pendingAmount = transactions
    .filter(t => ['pending', 'initiated'].includes(t.status))
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-payments">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-stone-200">
            <CardContent className="p-4">
              <p className="text-sm text-stone-500">Total Collected</p>
              <p className="text-2xl font-bold text-green-700" data-testid="total-collected">R{totalRevenue.toFixed(2)}</p>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-4">
              <p className="text-sm text-stone-500">Pending (Suspension)</p>
              <p className="text-2xl font-bold text-yellow-700" data-testid="total-pending">R{pendingAmount.toFixed(2)}</p>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-4">
              <p className="text-sm text-stone-500">Total Transactions</p>
              <p className="text-2xl font-bold text-stone-900" data-testid="total-transactions">{transactions.length}</p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-stone-200 bg-blue-50">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-800 mb-1">Paystack Test Mode</h3>
                <p className="text-sm text-blue-700 mb-2">
                  Use the following test card to simulate payments:
                </p>
                <div className="bg-white rounded-lg p-3 inline-flex items-center gap-3">
                  <code className="text-sm font-mono text-stone-900">4084 0840 8408 4081</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => {
                      navigator.clipboard.writeText('4084084084084081');
                      toast.success('Card number copied!');
                    }}
                    data-testid="copy-test-card"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  Expiry: Any future date | CVV: 408 | PIN: 0000 | OTP: 123456
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {transactions.length === 0 ? (
          <Card className="border-stone-200">
            <CardContent className="py-12 text-center">
              <CreditCard className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 font-medium">No transactions yet</p>
              <p className="text-sm text-stone-400 mt-1">Payments will appear here when customers complete checkout</p>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <table className="w-full admin-table">
              <thead className="bg-stone-50">
                <tr>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Payment Status</th>
                  <th>Order Status</th>
                  <th>Date</th>
                  <th>Session ID</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn) => (
                  <tr key={txn.id} data-testid={`txn-row-${txn.id}`}>
                    <td>
                      <div>
                        <p className="font-medium text-stone-900 text-sm">{txn.customer_name || 'N/A'}</p>
                        <p className="text-xs text-stone-500">{txn.customer_email || ''}</p>
                      </div>
                    </td>
                    <td className="font-semibold text-green-700">
                      R{(txn.amount || 0).toFixed(2)}
                    </td>
                    <td>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[txn.payment_status] || statusColors[txn.status] || 'bg-stone-100 text-stone-600'}`}>
                        {statusIcons[txn.payment_status] || statusIcons[txn.status] || <Clock className="w-3 h-3" />}
                        {txn.payment_status || txn.status || 'unknown'}
                      </span>
                    </td>
                    <td>
                      <span className="text-sm text-stone-600">{txn.order_status || 'N/A'}</span>
                    </td>
                    <td className="text-sm text-stone-500">
                      {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      <code className="text-xs text-stone-500 font-mono">
                        {txn.session_id ? txn.session_id.slice(0, 12) + '...' : 'N/A'}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Card className="border-stone-200 bg-yellow-50">
          <CardContent className="p-5">
            <h3 className="font-semibold text-yellow-800 mb-2">About the Suspension Account</h3>
            <p className="text-sm text-yellow-700">
              All customer payments are held in a suspension account until orders are confirmed and fulfilled.
              If an item is out of stock, the payment for that item is automatically released/refunded.
              In production, funds will be transferred to your bank account via Stripe Connect after order fulfillment.
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminPayments;
